import React, { Suspense } from "react";
import { useLocale, useTranslations } from "next-intl";
import { format } from "date-fns";
import queryString from "query-string";
import cx from "classnames";
import { useQuery } from "@tanstack/react-query";

import { useDebounce } from "@uidotdev/usehooks";

import Icon from "@ide/shared/components/Icon";
import BlurModal from "@/components/BlurModal";
import PropertyFilters from "@/components/PropertyFilters";
import Link from "@/components/Link";
import LoadingSkeleton from "@/components/LoadingSkeleton";

import {
  generateSearchVariations,
  // cleanQuery,
  capitalizeFirstLetter,
  pick,
} from "@ide/shared/lib/helpers";

import * as searchApi from "@ide/shared/api/search";
import * as suggestApi from "@ide/shared/api/suggest";

import usePropertySearch from "@/hooks/usePropertySearch";
import useModalStore from "@/stores/modal";
import { usePathname, useSearchParams, useRouter } from "next/navigation";

// https://dribbble.com/shots/20402416-Command-K-Menu-AI

const PropertySearchModal = ({
  show = false,
  // areas = [],
  showFilters: _showFilters = false,
  // filters = [
  //   "area",
  //   "unit_types",
  //   "size",
  //   "completion",
  //   "number_of_bedrooms",
  //   "price_range",
  //   "amenities",
  //   "payment_plan",
  //   "distances",
  //   "investment",
  // ],
  onClose = () => {},
  // onReset = () => {},
  // onSubmit = () => {},
}) => {
  const t = useTranslations();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [showFilters, setShowFilters] = React.useState(_showFilters);
  const [locationInputIsFocused, setLocationInputIsFocused] = React.useState(_showFilters);

  React.useLayoutEffect(() => {
    setShowFilters(_showFilters);
  }, [_showFilters]);

  const { values, errors, setFieldValue, resetForm, handleSubmit } = usePropertySearch();

  const debounced_areasOrProjects = useDebounce(values?._areasOrProjects, 200);

  const {
    data: { data: areasOrProjectsSuggestions },
  } = useQuery({
    queryKey: ["areasOrProjects", debounced_areasOrProjects],
    queryFn: () => {
      if (!debounced_areasOrProjects) {
        if (!values?._areasOrProjects) {
          return { data: [null, null, null, null, null, null] };
        }
        return suggestApi.areasOrProjects(values?._areasOrProjects);
      }
      return suggestApi.areasOrProjects(debounced_areasOrProjects);
    },
    // enabled: !!debounced_areasOrProjects,
    placeholderData: (prevData) => prevData ?? { data: [null, null, null, null, null, null] },
    refetchOnWindowFocus: false,
  });

  // https://nextjs.org/docs/app/api-reference/functions/use-router#router-events
  React.useEffect(() => {
    // const url = `${pathname}?${searchParams}`;
    // console.log(url);
    // You can now use the current URL
    // ...
    onClose();
  }, [pathname, searchParams]);

  // onReset={() => {
  //   closeSearchModal();
  //   resetForm();
  // }}
  // onSubmit={() => {
  //   closeSearchModal();
  //   handleSubmit();
  // }}

  return (
    <BlurModal
      className="modal-blur"
      overlayClassName="modal-blur-overlay"
      isOpen={show}
      onRequestClose={onClose}
    >
      <div className="absolute inset-0 md:py-16 w-full h-full pointer-events-none">
        <div className="relative h-full max-sm:pt-12 md:max-w-3xl mx-auto">
          <div className="left-4 top-2 absolute sm:hidden">
            <button
              className="cursor-pointer w-8 h-8 flex justify-center items-center bg-white rounded-full shadow-xl"
              onClick={onClose}
            >
              <Icon icon="fa-regular fa-xmark" />
            </button>
          </div>

          <div className="max-sm:h-full sm:h-full max-h-full flex flex-col bg-white pointer-events-auto shadow-xl rounded-t-xl md:rounded-xl">
            {/* <div className="contents"> */}
            <header className="h-16 shrink-0 relative z-50 border-b">
              <label className="h-full flex items-center">
                <div className="w-12 h-8 pl-4 mr-2 flex justify-center items-center">
                  <Icon icon="fa-regular fa-magnifying-glass" />
                </div>
                <input
                  type="text"
                  placeholder="Project, area, community"
                  className="h-8 flex-1 pr-4 outline-hidden"
                  value={
                    values.search_by_map && !locationInputIsFocused
                      ? "Map area"
                      : values._areasOrProjects
                  }
                  onChange={({ target }) => setFieldValue("_areasOrProjects", target.value)}
                  onFocus={() => {
                    setLocationInputIsFocused(true);
                    setShowFilters(false);
                  }}
                  onBlur={() => setLocationInputIsFocused(false)}
                  autoFocus={!showFilters}
                />

                {(values._areasOrProjects || values.search_by_map) && (
                  <button
                    className="pr-4 flex justify-center items-center"
                    onClick={() => {
                      setFieldValue("_areasOrProjects", "");
                      setFieldValue("search_by_map", false);

                      setFieldValue("any_areas", undefined);
                      setFieldValue("developers", undefined);
                      setFieldValue("projects", undefined);
                    }}
                  >
                    <Icon icon="fa-regular fa-circle-xmark" />
                  </button>
                )}
              </label>
            </header>
            <div className="flex-1 overflow-y-auto">
              <div
                className={cx("flex-1 overflow-y-scroll", {
                  "px-2": !showFilters,
                  "px-4": showFilters,
                })}
              >
                <div className="h-4"></div>

                {!showFilters &&
                  values?._areasOrProjects &&
                  Array.isArray(areasOrProjectsSuggestions) && (
                    <ul>
                      {areasOrProjectsSuggestions.map((suggestion, i) => {
                        if (suggestion === null) {
                          return (
                            <li
                              className="flex items-center w-full gap-2 p-2"
                              key={`search-modal-loading-skeleton-${i}`}
                            >
                              <div className="w-8 h-8">
                                <LoadingSkeleton></LoadingSkeleton>
                              </div>

                              <div className="flex flex-col flex-1 gap-2">
                                <div className="h-4 w-1/3">
                                  <LoadingSkeleton className="rounded-sm"></LoadingSkeleton>
                                </div>

                                <div className="h-3 w-2/3">
                                  <LoadingSkeleton className="rounded-sm"></LoadingSkeleton>
                                </div>
                              </div>
                            </li>
                          );
                        }

                        const { name, type, developer, location } = suggestion;

                        return (
                          <li key={`search-filter-areas-or-projects:${type}:${name}`}>
                            <button
                              // TODO make it highlighted when selected
                              className={cx(
                                "flex items-center gap-2 w-full p-2 whitespace-nowrap hover:bg-gray-100 rounded-lg text-left",
                                {
                                  "bg-neutral-100":
                                    (type === "area" && values?.any_areas?.includes(name)) ||
                                    (type === "master_project" &&
                                      values?.any_areas?.includes(name)) ||
                                    (type === "project" &&
                                      values?.projects?.includes(name) &&
                                      values?.developers?.includes(developer)),
                                },
                              )}
                              onClick={() => {
                                setShowFilters(true);

                                switch (type) {
                                  case "master_project":
                                  case "area":
                                    return resetForm({
                                      values: {
                                        _areasOrProjects: name,
                                        any_areas: [name],
                                        developers: undefined,
                                        projects: undefined,
                                      },
                                    });
                                  case "project":
                                    resetForm({
                                      values: {
                                        _areasOrProjects: name,
                                        projects: [name],
                                        developers: [developer],
                                        availability: ["available", "prelaunch", "sold_out"],
                                      },
                                    });
                                }
                              }}
                            >
                              <div className="w-8 h-8 flex justify-center items-center bg-neutral-200 rounded-lg">
                                <Icon
                                  icon={(() => {
                                    switch (type) {
                                      case "master_project":
                                      case "area":
                                        return "fa-regular fa-location-dot";
                                      case "developer":
                                        return "fa-regular fa-user-helmet-safety";
                                      case "project":
                                        return "fa-regular fa-tree-city";
                                    }
                                  })()}
                                  className="text-gray-900"
                                  fontSize={16}
                                />
                              </div>
                              <div className="flex flex-col flex-1">
                                <div>{type === "developer" ? developer : name}</div>
                                <div className="text-xs text-neutral-500">
                                  {(type === "area" || type === "master_project") &&
                                    "Area in Dubai"}
                                  {type === "project" && `Project in ${location}, by ${developer}`}
                                </div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                {showFilters && (
                  <PropertyFilters values={values} errors={errors} setFieldValue={setFieldValue} />
                )}

                <div className="h-4"></div>
              </div>
            </div>

            <footer className="border-t">
              <div className="p-4 flex justify-center items-center">
                {!showFilters && (
                  <button
                    onClick={() => setShowFilters(true)}
                    className="px-6 py-2 rounded-lg bg-gray-400 text-white font-medium"
                  >
                    Filters
                  </button>
                )}
                {showFilters && (
                  <button
                    onClick={() => {
                      onClose();
                      if (pathname === "/map") {
                        router.push("/map");
                      }
                    }}
                    className="px-6 py-2 rounded-lg bg-gray-400 text-white font-medium"
                  >
                    Clear all
                  </button>
                )}
                <div className="flex-1"></div>
                <button
                  onClick={() => {
                    handleSubmit();
                  }}
                  className="btn-cta rounded-lg!"
                  type="submit"
                >
                  Search
                </button>
              </div>
            </footer>
            {/* </div> */}
          </div>
        </div>
      </div>
    </BlurModal>
  );
};

const SuspensefulPropertySearchModal = (props) => (
  <Suspense>
    <PropertySearchModal {...props} />
  </Suspense>
);

export default SuspensefulPropertySearchModal;
